import React from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Text } from '@mafc/trolly';
import COLORS from 'theme/colors';
import { Information } from '@mafc/icon';
import {
  IOrderConfirmationPaymentInfo,
  IOrderConfirmationShipment,
} from 'pages/OrderConfirmation/OrderConfirmation.types';
import {
  AppliedVoucher,
  DeliveryCost,
  LoyaltyVoucher,
  PlasticBagPackingInfo,
} from 'network/response/ICart';
import { EShipmentTypes } from 'services/shipments/types/shipments.types';
import ShipmentOrderSummary from './ShipmentOrderSummary';
import CardDivider from 'pages/Checkout/components/CheckoutOrderSummary/snippets/CardDivider';
import FeeOrderSummary from './FeeOrderSummary';
import DiscountOrderSummary from './DiscountOrderSummary';
import PaymentOrderSummary from './PaymentOrderSummary';
import { useFeature } from '@mafc/appshell';
import { getPriceInUSD, IFinalPrice } from 'utils/price.util';
import PriceInUSD from 'common/PriceInUSD';
import OrderSummaryRow from 'common/OrderSummary/components/OrderSummaryRow';
import useApigeeLabels from 'common/ApigeeLabels/hooks/useApigeeLabels';
import SummaryRowTypes from 'common/OrderSummary/components/OrderSummaryRow/row.types';
import { useSelector } from 'react-redux';
import { IAppState } from '@mafc/common';
import { CurrencyCodes } from 'pages/Checkout/constants/checkout.constant';

const DetailedOrderSummary: React.FC<{
  appliedVouchers: AppliedVoucher[];
  codFee: DeliveryCost;
  loyaltyVouchers: LoyaltyVoucher[];
  totalAppliedDiscounts: IFinalPrice;
  totalBundleDiscount?: IFinalPrice;
  shipments: IOrderConfirmationShipment[];
  paymentMode: string;
  paymentInfo: IOrderConfirmationPaymentInfo;
  sustainabilityConsent: boolean | null;
  subTotal: IFinalPrice;
  plasticBagPackingInfo: PlasticBagPackingInfo;
  totalFees?: IFinalPrice;
  totalPriceWithTax?: IFinalPrice;
}> = ({
  appliedVouchers,
  codFee,
  subTotal,
  loyaltyVouchers,
  totalAppliedDiscounts,
  paymentMode,
  paymentInfo,
  shipments,
  sustainabilityConsent,
  plasticBagPackingInfo,
  totalFees,
  totalPriceWithTax,
  totalBundleDiscount,
}) => {
  const { t } = useTranslation();
  const { totalAmountDue } = useApigeeLabels();
  const currencyRate = useSelector(
    (state: IAppState) => state.appConfig && state.appConfig.currencyRate
  );

  const hasCheckoutExpressFreeDeliveryMsg = useFeature('checkout-express-free-delievery-msg').on;
  const isExpressShipment = shipments.find(shipment => shipment.type === EShipmentTypes.EXPRESS);

  const value = totalPriceWithTax ? totalPriceWithTax.value : 0;

  const priceInUSD = {
    currencyIso: CurrencyCodes.USD,
    currencyName: CurrencyCodes.USD,
    value: getPriceInUSD(value, currencyRate),
  };

  const isCurrencyUSD = sessionStorage.getItem('changeCurrencyToUSD');

  return (
    <Box>
      <ShipmentOrderSummary shipments={shipments} subTotal={subTotal} />

      <FeeOrderSummary
        shipments={shipments}
        codFee={codFee}
        paymentMode={paymentMode}
        sustainabilityConsent={sustainabilityConsent}
        plasticBagPackingInfo={plasticBagPackingInfo}
        totalFees={totalFees}
      />

      <DiscountOrderSummary
        appliedVouchers={appliedVouchers}
        totalBundleDiscount={totalBundleDiscount}
        loyaltyVouchers={loyaltyVouchers}
        totalAppliedDiscounts={totalAppliedDiscounts}
      />

      <PaymentOrderSummary totalPriceWithTax={totalPriceWithTax} paymentInfo={paymentInfo} />

      <OrderSummaryRow
        label={totalAmountDue}
        priceDetails={isCurrencyUSD ? priceInUSD : totalPriceWithTax}
        variant={SummaryRowTypes.header}
      />

      {/* It won't be displayed on the order confirmation page as totalPriceWithTax is not being passed on OC */}
      {totalPriceWithTax && !isCurrencyUSD && (
        <PriceInUSD price={totalPriceWithTax} variant="viewDetailDrawer" />
      )}

      {isExpressShipment && hasCheckoutExpressFreeDeliveryMsg && (
        <>
          <CardDivider mb={12} width={1} bg={COLORS.MERCURY} />

          <Box mb={2} display="flex" alignItems="start">
            <Box
              as="span"
              sx={{
                maxWidth: '19px',
                width: '10%',
              }}
            >
              <Information size="XS" />
            </Box>

            <Box ml={1}>
              <Text
                as="span"
                mr={1}
                fontSize={1}
                color={COLORS.BLACK}
                fontWeight={6}
                display={['block', null, null, 'none']}
              >
                {t('orderConfirmation.orderSummary.declarationLine1')}:
              </Text>
              <Text as="span" fontSize={1} color={COLORS.BLACK}>
                {t('orderConfirmation.orderSummary.declarationLine2')}
              </Text>
              <Text as="span" mx={1} fontSize={1} color={COLORS.BLACK} fontWeight={6}>
                {t('orderConfirmation.orderSummary.declarationLine3')}
              </Text>
              <Text as="span" fontSize={1} color={COLORS.BLACK}>
                {t('orderConfirmation.orderSummary.declarationLine4')}
              </Text>
            </Box>
          </Box>
        </>
      )}
    </Box>
  );
};

export default DetailedOrderSummary;
